import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AccountPage } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { useAuth } from "../auth/AuthContext.jsx";
import { errorText } from "../ui/Feedback.jsx";

/**
 * AccountScreen wires the account page to the API.
 *
 * The screen is where the verification banner sends people, and where the second
 * factor is chosen — including the SMS option, which is the only reason a phone
 * number is ever asked for now.
 */
export default function AccountScreen() {
  const api = useApiClient();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);

  function failed(error, fallback) {
    setMessage({ tone: "danger", text: errorText(error, fallback) });
  }

  async function reload(next) {
    await refresh?.();
    setMessage(next);
  }

  return (
    <div>
      <AccountPage
        user={user}
        message={message}
        onResendVerification={async () => {
          try {
            await api.auth.resendTwoFactor({
              challenge_id: user?.id,
              purpose: "verify_email",
            });
          } catch (error) {
            failed(error, "Could not send a new code.");
            return;
          }
          // The code is entered on the shared code screen, which knows how to
          // handle a resend and an expired code.
          navigate("/2fa", {
            state: {
              challengeId: user?.id,
              destination: user?.email,
              purpose: "verify_email",
              nextPath: "/account",
            },
          });
        }}
        onChangeTimezone={async (timezone) => {
          await api.me.updateTimezone(timezone);
          // Reloaded rather than patched locally: the zone decides how every
          // instant on every screen is printed, so the bootstrap view is the
          // thing that has to change.
          await reload();
        }}
        onChangePassword={async ({ currentPassword, newPassword }) => {
          try {
            await api.me.changePassword({ currentPassword, newPassword });
          } catch (error) {
            failed(error, "Could not change your password.");
            return;
          }
          setMessage({
            tone: "success",
            text: "Password changed. Every other device was signed out.",
          });
        }}
        onEnableTotp={() => api.me.enableTwoFactor({ method: "totp" })}
        onEnableSms={async (phone) => {
          try {
            return await api.me.enableTwoFactor({ method: "sms", phone });
          } catch (error) {
            failed(error, "Could not send an SMS code to that number.");
            throw error;
          }
        }}
        onConfirmTwoFactor={async (code) => {
          try {
            await api.me.confirmTwoFactor(code);
          } catch (error) {
            failed(error, "That code was not accepted.");
            throw error;
          }
          await reload({ tone: "success", text: "Two-factor authentication is on." });
        }}
        onRequestDisableCode={async () => {
          try {
            await api.me.requestDisableCode();
          } catch (error) {
            failed(error, "Could not send the code.");
            throw error;
          }
          setMessage({ tone: "success", text: "We sent you a code." });
        }}
        onDisableTwoFactor={async (code) => {
          try {
            await api.me.disableTwoFactor(code);
          } catch (error) {
            failed(error, "That code was not accepted.");
            throw error;
          }
          await reload({ tone: "success", text: "Two-factor authentication is off." });
        }}
      />
    </div>
  );
}
